import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Cpu, Layers, Printer, Users, Factory, Ruler } from "lucide-react";
import { BIRDS } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

const STATS = [
  { k: "05", label: "Print Technologies", Icon: Layers },
  { k: "03", label: "Offset Presses", Icon: Printer },
  { k: "10+", label: "Digital Printers", Icon: Cpu },
  { k: "30+", label: "Print Specialists", Icon: Users },
];

const MACHINES = [
  { name: "Komori Sheet-Fed", spec: "1 Colour · Offset" },
  { name: "Sahil Graphics", spec: "1 Colour · Offset" },
  { name: "Auto Print", spec: "1 Colour · Offset" },
  { name: "Xerox Versant 280", spec: "4 Colour · Digital" },
  { name: "Fujifilm Apeos C3570", spec: "4 Colour · Digital" },
  { name: "Fujifilm Apeos C550", spec: "1 Colour · Digital" },
  { name: "Epson WF-C869R", spec: "4 Colour · Digital" },
  { name: "Epson M-21000", spec: "Mono · Digital" },
  { name: "Epson Surecolour", spec: "4 Colour · Large Format" },
  { name: "Allwin Jumbo", spec: "4 Colour · Eco-Solvent" },
  { name: "Skycut V48", spec: "Digital Plotter" },
  { name: "Shoei 56 / Ethocut", spec: "Folding · Cutting" },
];

export default function Infrastructure() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 18]);

  return (
    <section
      id="infrastructure"
      ref={ref}
      data-testid="infrastructure-section"
      className="relative bg-[#0A0A0A] text-[#F4F4F0] border-t border-black overflow-hidden"
    >
      {/* Halftone motif */}
      <div className="pointer-events-none absolute inset-0 halftone text-white/[0.04]" />

      {/* Parallax bird motifs */}
      <motion.img
        src={BIRDS.bird_composition}
        alt=""
        aria-hidden
        style={{ y, rotate }}
        className="pointer-events-none absolute -right-[8%] top-[4%] w-[38vw] max-w-[520px] opacity-90 select-none"
      />
      <motion.img
        src={BIRDS.bird_soar}
        alt=""
        aria-hidden
        style={{ y: useTransform(scrollYProgress, [0, 1], ["10%", "-14%"]) }}
        className="pointer-events-none absolute -left-[6%] bottom-[8%] w-[26vw] max-w-[360px] opacity-70 select-none"
      />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
          <div className="md:col-span-6">
            <div className="flex items-center gap-4 mb-8 text-[#F4F4F0]/70">
              <span className="w-10 h-px bg-[#F4F4F0]/60" />
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase">
                {t("infra.chapter")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              {t("infra.title_1")} <br />
              <em className="italic">{t("infra.title_2_italic")}</em> <br />
              {t("infra.title_3")}
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-base md:text-lg leading-relaxed text-[#F4F4F0]/80 max-w-[52ch]">
              {t("infra.sub_before")}{" "}
              <span className="text-[color:var(--ap-cyan)]">Xerox Versant</span>{" "}
              &{" "}
              <span className="text-[color:var(--ap-yellow)]">
                Fujifilm Apeos
              </span>
              {t("infra.sub_after")}
            </p>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-white/20">
          {STATS.map((s, i) => {
            const { Icon } = s;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="border-r border-b border-white/20 p-6 md:p-10"
                data-testid={`infra-stat-${i}`}
              >
                <Icon className="w-5 h-5 opacity-80" />
                <div className="mt-4 font-display text-6xl md:text-7xl leading-none">
                  {s.k}
                </div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                  {s.label}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Blueprint / machine ledger */}
        <div className="mt-20 border-t border-white/20 pt-10">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Factory className="w-4 h-4 opacity-70" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/70">
                {t("infra.ledger")}
              </span>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Ruler className="w-4 h-4 opacity-70" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/70">
                {t("infra.capacity")}
              </span>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {MACHINES.map((m, i) => (
              <motion.li
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-5% 0px" }}
                transition={{ duration: 0.5, delay: i * 0.035 }}
                className="group border-b border-white/15 py-4 flex items-baseline justify-between gap-6"
                data-testid={`infra-machine-${i}`}
              >
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/45 w-8">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-xl md:text-2xl flex-1 tracking-tight group-hover:text-[color:var(--ap-yellow)] transition-colors">
                  {m.name}
                </span>
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F4F4F0]/60">
                  {m.spec}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
